// Release data for the download page: which builds exist, where they come
// from, and what each platform needs. The Godot app's release workflow
// (steadily-godot/.github/workflows/release.yml) publishes binaries.json
// alongside its auto-update feed; the store listings are static links.
import { Monitor, Laptop, Terminal, Smartphone, Tablet, Cpu } from 'lucide-react';

// Release channel configuration — the Godot app's binaries.json, published
// by the steadily-godot release workflow alongside its auto-update feed.
export const RELEASES_CONFIG = {
  stable: {
    baseUrl: 'https://releases.steadily.me/godot-stable',
    label: 'Stable',
  },
  beta: {
    baseUrl: 'https://releases.steadily.me/godot-beta',
    label: 'Beta',
  },
};

// Format file size for display
export const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

// Best-effort OS detection for the download hero. Returns one of
// 'windows' | 'mac' | 'linux' | 'android' | 'ios' | null. We disambiguate
// modern iPadOS — which reports as MacIntel — by checking touch points.
// Returns null where there is no navigator (the build-time prerender).
export const detectOS = () => {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  if (/iPhone|iPad|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Mac/.test(platform)) return 'mac';
  if (/Win/.test(platform)) return 'windows';
  if (/Linux/.test(platform)) return 'linux';
  return null;
};

// Per-platform metadata for the download page. `accent` colors the
// icon chip; `heroTagline` is the H2 inside the hero card; `qualifier`
// is the small line beneath it. `fileKey` names the platform entry in
// binaries.json's `platforms` map.
export const PLATFORM_META = {
  windows:     { label: 'Windows',     fileKey: 'windows-x64', accent: '#0078D4', heroTagline: 'Ready for your Windows PC',    qualifier: 'x64 · Windows 10+' },
  mac:         { label: 'macOS',       fileKey: 'macos',       accent: '#2B2626', heroTagline: 'Built for your Mac',           qualifier: 'Universal · Apple Silicon & Intel' },
  linux:       { label: 'Linux',       fileKey: 'linux-x64',   accent: '#E95420', heroTagline: 'Made for your Linux machine',  qualifier: 'Intel/AMD 64-bit' },
  'linux-arm': { label: 'Linux (ARM)', fileKey: 'linux-arm64', accent: '#E95420', heroTagline: 'Made for your Linux machine',  qualifier: 'ARM64 · Raspberry Pi ready' },
  android:     { label: 'Android',     fileKey: 'android',     accent: '#3DDC84', heroTagline: 'On your Android phone',         qualifier: 'APK · Android 7.0+ · install via sideload' },
};

export const PLATFORM_ICONS = {
  windows: Monitor,
  mac: Laptop,
  linux: Terminal,
  'linux-arm': Terminal,
  android: Smartphone,
};

// Store listing URLs. Play and Galaxy Store are derived from the package
// name. A null URL renders the badge disabled with a "Coming soon" tooltip
// — the Mac App Store link stays null until that listing goes live (it
// needs the numeric app ID from App Store Connect). The iOS listing went
// live 2026-09-05 (Steadily.me, id6802067747, seller Reality Moss LLC).
export const STORE_LINKS = {
  iosAppStore: 'https://apps.apple.com/us/app/steadily-me/id6802067747',
  macAppStore: null, // TODO: 'https://apps.apple.com/app/id<MAC_APP_STORE_ID>'
  playStore: 'https://play.google.com/store/apps/details?id=com.realitymoss.steadily',
  // Galaxy Store: nothing is registered yet (Seller Portal needs Commercial
  // Seller status first — see steadily-godot/store/galaxy/listing.md). Flip
  // to the URL below the day the listing goes live.
  galaxyStore: null, // TODO: 'https://galaxystore.samsung.com/detail/com.realitymoss.steadily'
};

// Minimum requirements per platform. Numbers come from the Godot export
// config (Android min SDK 24, iOS 15.0) and the engine's GL Compatibility
// renderer floor (OpenGL 3.3 desktop / OpenGL ES 3.0 mobile). `stores`
// renders as store-badge links ahead of the direct-download button;
// `guide` links the card to its section of the always-on display setup page.
export const MIN_REQUIREMENTS = [
  {
    key: 'ios',
    label: 'iOS & iPadOS',
    icon: Tablet,
    accent: '#0A84FF',
    headline: 'iOS / iPadOS 15 or later',
    details: ['iPhone & iPad', 'Free on the App Store'],
    examples: 'iPhone 6s (2015) and newer; iPad 5th gen (2017), iPad Air 2, iPad mini 4, and anything newer.',
    stores: [{ label: 'Download on the App Store', url: STORE_LINKS.iosAppStore, badge: '/images/badges/app-store.svg', badgeClass: 'h-10' }],
  },
  {
    key: 'android',
    label: 'Android',
    icon: Smartphone,
    accent: '#3DDC84',
    headline: 'Android 7.0 (2016) or newer',
    details: ['32-bit and 64-bit ARM', 'No Google services required — Fire OS works', '2 GB RAM'],
    examples: 'Samsung Galaxy S7 or newer, Pixel phones, Amazon Fire HD 8 & 10 tablets, and most tablets sold since 2016.',
    stores: [
      // Google's generic badge PNG bakes its required clear space into the
      // image, so it renders taller with negative margins to line up with
      // the Apple badges' 40px artwork.
      { label: 'Get it on Google Play', url: STORE_LINKS.playStore, badge: '/images/badges/google-play.png', badgeClass: 'h-[60px] -my-2.5' },
      { label: 'Available on Galaxy Store', url: STORE_LINKS.galaxyStore, badge: '/images/badges/galaxy-store.png', badgeClass: 'h-10' },
    ],
    guide: '/install#android',
  },
  {
    key: 'windows',
    label: 'Windows',
    icon: Monitor,
    accent: '#0078D4',
    headline: 'Windows 10 or 11',
    details: ['64-bit (x64)', 'OpenGL 3.3 graphics — any GPU from roughly 2010 onward', '2 GB RAM'],
    examples: 'Pretty much any PC or laptop from the last decade, including budget machines and older Surface devices.',
    guide: '/install#windows',
  },
  {
    key: 'mac',
    label: 'macOS',
    icon: Laptop,
    accent: '#2B2626',
    headline: 'macOS 10.13 High Sierra or later',
    details: ['Universal binary — Apple Silicon & Intel', '2 GB RAM'],
    examples: 'Macs from around 2010 onward, like a 2013 MacBook Air or a 2012 iMac.',
    stores: [{ label: 'Download on the Mac App Store', url: STORE_LINKS.macAppStore, badge: '/images/badges/mac-app-store.svg', badgeClass: 'h-10' }],
    guide: '/install#mac',
  },
  {
    key: 'linux',
    label: 'Linux',
    icon: Terminal,
    accent: '#E95420',
    headline: 'Any mainstream 64-bit distro',
    details: ['Intel/AMD 64-bit (x86_64)', 'glibc-based — Ubuntu, Fedora, Debian, Mint…', 'OpenGL 3.3 graphics'],
    examples: 'A thrift-store ThinkPad running Mint is more than enough.',
    guide: '/install#linux',
  },
  {
    key: 'linux-arm',
    label: 'Linux (ARM)',
    icon: Cpu,
    accent: '#E95420',
    headline: 'ARM64 with a 64-bit OS',
    details: ['aarch64 · glibc-based distro', 'OpenGL ES 3.0 graphics'],
    examples: 'Raspberry Pi 4 or 5, or similar single-board computers — perfect for an always-on display.',
    guide: '/install#linux',
  },
];

// Pull the platform's {url, sizeBytes} entry out of binaries.json.
export const getPlatformFile = (platform, info) =>
  info?.platforms?.[PLATFORM_META[platform].fileKey] || null;
